import React, { useState, useEffect } from 'react';
import './ReviewDetailDialog.css';
import AiResultCard from './AiResultCard';
import { getReview as apiGetReview, saveReview as apiSaveReview } from './services/apiService';
import { AI_PROVIDER } from './constants/ai';

/**
 * Review Detail Dialog — modal-stack component
 *
 * Two modes:
 *   1) reviewId       — fetch existing review from DB, read-only (no Save)
 *   2) generatedData  — just-generated preview, shows [Save Review] button
 *
 * Three entry points use this:
 *   - AI REVIEW List → click [View] on existing review (mode 1)
 *   - AI REVIEW Generate → after analyze completes (mode 2)
 *   - AI PROFILE → click [View] on existing review (mode 1)
 *
 * Save flow (mode 2):
 *   [Save Review] → [Saving...] → [Saved] (disabled)
 *   onSaved fires immediately on success so parent can refresh its review
 *   list in the background while the user keeps reading the detail.
 *   The dialog stays open — user closes manually with X (consistent with AI SCAN).
 */
const timeRangeLabel = (tr) => {
  const labels = { day: 'Daily', week: 'Weekly', month: 'Monthly', all: 'All', custom: 'Custom' };
  return labels[tr] || (tr ? tr.charAt(0).toUpperCase() + tr.slice(1) : '');
};

function ReviewDetailDialog({ reviewId, generatedData, onSaved, onClose }) {
  const [meta, setMeta] = useState(generatedData?.reviewMeta || null);
  const [anthropicResult, setAnthropicResult] = useState(generatedData?.anthropicResult || null);
  const [openaiResult, setOpenaiResult] = useState(generatedData?.openaiResult || null);
  const [isLoading, setIsLoading] = useState(!!reviewId);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedMeta, setSavedMeta] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const isUnsaved = !!generatedData && !isSaved;

  useEffect(() => {
    if (!reviewId) return;
    let cancelled = false;
    setIsLoading(true);
    apiGetReview(reviewId)
      .then(r => {
        if (cancelled) return;
        setMeta({
          id: r.id,
          reviewDate: r.reviewDate,
          timeRange: r.timeRange,
          dateStart: r.dateStart,
          dateEnd: r.dateEnd,
        });
        setAnthropicResult(r.anthropicResult ? (typeof r.anthropicResult === 'string' ? JSON.parse(r.anthropicResult) : r.anthropicResult) : null);
        setOpenaiResult(r.openaiResult ? (typeof r.openaiResult === 'string' ? JSON.parse(r.openaiResult) : r.openaiResult) : null);
        setIsLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load review:', err);
        setLoadError(err.message || 'Failed to load review');
        setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [reviewId]);

  const handleSave = async () => {
    if (isSaving || isSaved || !generatedData) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const saved = await apiSaveReview(
        generatedData.reviewMeta.reviewDate,
        generatedData.reviewMeta.timeRange,
        generatedData.reviewMeta.dateStart,
        generatedData.reviewMeta.dateEnd,
        generatedData.anthropicResult,
        generatedData.openaiResult,
        generatedData.extractedProfile,
      );
      setIsSaved(true);
      setSavedMeta({ ...saved, timeRange: generatedData.reviewMeta.timeRange });
      if (onSaved) onSaved(saved);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveError(err.message || 'Failed to save review');
    } finally {
      setIsSaving(false);
    }
  };

  const getTitle = () => {
    if (isLoading) return 'Loading review...';
    if (isUnsaved) {
      const trLabel = timeRangeLabel(meta?.timeRange);
      return `New ${trLabel} Review (Unsaved) · ${meta?.reviewDate || ''}`;
    }
    const m = savedMeta || meta;
    if (m && m.id) {
      const trLabel = timeRangeLabel(m.timeRange);
      return `${trLabel} Review #${m.id} · ${m.reviewDate}`;
    }
    return `Review #${reviewId || ''}`;
  };

  return (
    <div className="review-detail-overlay" onClick={onClose}>
      <div className="review-detail-dialog" onClick={e => e.stopPropagation()}>
        <div className="review-detail-header">
          <h3>{getTitle()}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="review-detail-content">
          {isLoading && (
            <div className="loading-state">
              <div className="loading-icon">⏳</div>
              <p>Loading review...</p>
            </div>
          )}

          {loadError && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p style={{ color: '#dc2626' }}>{loadError}</p>
            </div>
          )}

          {!isLoading && !loadError && (anthropicResult || openaiResult) && (
            <div className="ai-results-grid">
              {anthropicResult && <AiResultCard result={anthropicResult} provider={AI_PROVIDER.ANTHROPIC} />}
              {openaiResult && <AiResultCard result={openaiResult} provider={AI_PROVIDER.OPENAI} />}
            </div>
          )}
        </div>

        <div className="review-detail-footer">
          {saveError && (
            <div className="save-error">⚠️ {saveError}</div>
          )}
          {isUnsaved && (
            <button
              className={`save-review-btn ${isSaved ? 'saved' : ''}`}
              onClick={handleSave}
              disabled={isSaving || isSaved}
              title={isSaved ? 'Review saved to database' : 'Save this review to the database'}
            >
              {isSaving ? '⏳ Saving...' : '💾 Save Review'}
            </button>
          )}
          {isSaved && (
            <button className="save-review-btn saved" disabled>✓ Saved</button>
          )}
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default ReviewDetailDialog;
